import { cn } from "@design-system/utils";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import * as React from "react";

type AvatarVariant = "user" | "company";

interface AvatarContextValue {
  variant: AvatarVariant;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

const AvatarContext = React.createContext<AvatarContextValue>({
  variant: "user",
  isLoading: false,
  setIsLoading: () => {},
});

interface AvatarProps extends React.ComponentProps<
  typeof AvatarPrimitive.Root
> {
  variant?: AvatarVariant;
}

function Avatar({ className, variant = "user", ...props }: AvatarProps) {
  const [isLoading, setIsLoading] = React.useState(false);

  return (
    <AvatarContext.Provider value={{ variant, isLoading, setIsLoading }}>
      <AvatarPrimitive.Root
        data-slot="avatar"
        className={cn(
          "relative flex size-6 shrink-0 overflow-hidden",
          variant === "user" ? "rounded-full" : "rounded-md",
          className
        )}
        {...props}
      >
        {isLoading && <AvatarLoadingIndicator />}
        {props.children}
      </AvatarPrimitive.Root>
    </AvatarContext.Provider>
  );
}

function AvatarLoadingIndicator() {
  const { variant } = React.useContext(AvatarContext);

  return (
    <div
      data-slot="avatar-loading"
      className={cn(
        "bg-shape-neutral-filled-background absolute inset-0 size-full animate-pulse",
        variant === "user" ? "rounded-full" : "rounded-md"
      )}
    />
  );
}

function AvatarImage({
  className,
  onLoadingStatusChange,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  const { setIsLoading } = React.useContext(AvatarContext);

  const handleLoadingStatusChange = React.useCallback(
    (status: "idle" | "loading" | "loaded" | "error") => {
      setIsLoading(status === "loading");
      onLoadingStatusChange?.(status);
    },
    [setIsLoading, onLoadingStatusChange]
  );

  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn(
        "relative z-10 aspect-square size-full object-cover",
        className
      )}
      {...props}
      onLoadingStatusChange={handleLoadingStatusChange}
    />
  );
}

function AvatarFallback({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  const { variant } = React.useContext(AvatarContext);

  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        "bg-shape-neutral-filled-background text-shape-neutral-filled-foreground-muted flex size-full items-center justify-center text-center leading-[10px] font-medium",
        variant === "user"
          ? "rounded-full text-[10px]"
          : "rounded-md text-base leading-none",
        className
      )}
      {...props}
    />
  );
}

export { Avatar, AvatarImage, AvatarFallback };
export type { AvatarVariant };
